/**
 * QA API client.
 *
 * Every call goes through `apiFetch(..., { app: 'api' })` because the QA router
 * is mounted on the gen-api service, so it shares `VITE_APIGEN_API_BASE` (and the
 * `/apigen` dev proxy) rather than introducing a base URL of its own.
 */

import { apiFetch, APIGEN_API_KEY } from '../utils/api';
import type {
  QaHistoryResponse,
  QaLiveResponse,
  QaOptions,
  QaRun,
  QaRunAnalytics,
  QaRunCreatedResponse,
  QaRunListResponse,
  QaServedModelsResponse,
  QaTestDefinition,
  QaTestListResponse,
} from './types';

function qaHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(extra || {}) };
  if (APIGEN_API_KEY) {
    headers['X-API-Key'] = APIGEN_API_KEY;
  }
  return headers;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  let parsed: unknown = undefined;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    // A non-JSON body almost always means the request never reached the gen-api
    // service — it isn't running, or a dev proxy sent `/apigen/*` elsewhere.
    const snippet = text.slice(0, 80).replace(/\s+/g, ' ').trim();
    throw new Error(
      `QA service unreachable or misconfigured (HTTP ${res.status}, non-JSON response` +
        (snippet ? `: "${snippet}…"` : '') +
        '). Is the gen-api server running and VITE_APIGEN_API_BASE / the dev proxy set correctly?',
    );
  }
  if (!res.ok) {
    const body = parsed as { detail?: string | { msg?: string }[] } | undefined;
    const detail = body?.detail;
    const message = Array.isArray(detail)
      ? detail.map((d) => d?.msg).filter(Boolean).join('; ')
      : detail;
    throw new Error(`${res.status}: ${message || res.statusText}`);
  }
  return parsed as T;
}

/** Static options for the definition editor: which applications are reachable, limits. */
export async function fetchQaOptions(): Promise<QaOptions> {
  const res = await apiFetch('/v1/qa/config', { app: 'api', headers: qaHeaders() });
  return jsonOrThrow<QaOptions>(res);
}

export interface QaDefinitionPayload {
  name: string;
  description?: string | null;
  per_app_config: Record<string, unknown>;
  scan_ids_by_app: Record<string, string[]>;
}

/** Save a definition, or add a new version of one that already exists. */
export async function createQaTest(body: QaDefinitionPayload): Promise<QaTestDefinition> {
  const res = await apiFetch('/v1/qa/tests', {
    app: 'api',
    method: 'POST',
    headers: qaHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return jsonOrThrow<QaTestDefinition>(res);
}

/**
 * Save an edit. Definitions are append-only, so this returns a *new* version
 * rather than a mutated copy of `id`.
 */
export async function updateQaTest(
  id: string,
  body: QaDefinitionPayload,
): Promise<QaTestDefinition> {
  const res = await apiFetch(`/v1/qa/tests/${encodeURIComponent(id)}`, {
    app: 'api',
    method: 'PUT',
    headers: qaHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return jsonOrThrow<QaTestDefinition>(res);
}

/** Saved definitions. Current versions only unless `includeSuperseded`. */
export async function fetchQaTests(
  { includeSuperseded = false, limit = 200 } = {},
): Promise<QaTestDefinition[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (includeSuperseded) params.set('include_superseded', 'true');
  const res = await apiFetch(`/v1/qa/tests?${params}`, { app: 'api', headers: qaHeaders() });
  const data = await jsonOrThrow<QaTestListResponse>(res);
  return data.tests;
}

/**
 * Remove a test from the bank. Deletes every version of it — the bank shows only
 * the current version, so removing just that one would put the previous version
 * in its place and look like the delete had failed.
 *
 * Runs already in the history are untouched.
 */
export async function deleteQaTest(
  id: string,
): Promise<{ name: string; versions_deleted: number }> {
  const res = await apiFetch(`/v1/qa/tests/${encodeURIComponent(id)}`, {
    app: 'api',
    method: 'DELETE',
    headers: qaHeaders(),
  });
  return jsonOrThrow<{ name: string; versions_deleted: number }>(res);
}

export async function fetchQaTest(id: string): Promise<QaTestDefinition> {
  const res = await apiFetch(`/v1/qa/tests/${encodeURIComponent(id)}`, {
    app: 'api',
    headers: qaHeaders(),
  });
  return jsonOrThrow<QaTestDefinition>(res);
}

/**
 * Start a run. Returns as soon as the row exists, with the configuration as it
 * will *actually* run — the backend clamps the knobs, and anything it adjusted or
 * had to skip is spelled out in `warnings`.
 */
export async function createQaRun(definitionId: string): Promise<QaRunCreatedResponse> {
  const res = await apiFetch('/v1/qa/runs', {
    app: 'api',
    method: 'POST',
    headers: qaHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ definition_id: definitionId }),
  });
  return jsonOrThrow<QaRunCreatedResponse>(res);
}

export async function fetchQaRuns(limit = 100): Promise<QaRun[]> {
  const res = await apiFetch(`/v1/qa/runs?limit=${limit}`, { app: 'api', headers: qaHeaders() });
  const data = await jsonOrThrow<QaRunListResponse>(res);
  return data.runs;
}

export async function fetchQaRun(id: string): Promise<QaRun> {
  const res = await apiFetch(`/v1/qa/runs/${encodeURIComponent(id)}`, {
    app: 'api',
    headers: qaHeaders(),
  });
  return jsonOrThrow<QaRun>(res);
}

/**
 * One fan-out read of every application the run is loading. Server-side, so this
 * is a single request per poll no matter how many applications are involved.
 */
export async function fetchQaRunLive(id: string): Promise<QaLiveResponse> {
  const res = await apiFetch(`/v1/qa/runs/${encodeURIComponent(id)}/live`, {
    app: 'api',
    headers: qaHeaders(),
  });
  return jsonOrThrow<QaLiveResponse>(res);
}

export async function fetchQaRunAnalytics(
  id: string,
  bucketSeconds = 10,
): Promise<QaRunAnalytics> {
  const res = await apiFetch(
    `/v1/qa/runs/${encodeURIComponent(id)}/analytics?bucket=${bucketSeconds}`,
    { app: 'api', headers: qaHeaders() },
  );
  return jsonOrThrow<QaRunAnalytics>(res);
}

/** Per-model stats from generation-pipeline `model_api_attempts` rows. */
export async function fetchQaModelStats(
  from: Date,
  to: Date,
  includeStress = false,
  modelVersion?: string,
): Promise<QaServedModelsResponse> {
  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
    include_stress: String(includeStress),
  });
  if (modelVersion) params.set('model_version', modelVersion);
  const res = await apiFetch(`/v1/qa/model-stats?${params}`, {
    app: 'api',
    headers: qaHeaders(),
  });
  return jsonOrThrow<QaServedModelsResponse>(res);
}

/** Runs grouped by test name, newest group first. */
export async function fetchQaHistory(limit = 500): Promise<QaHistoryResponse> {
  const res = await apiFetch(`/v1/qa/history?limit=${limit}`, {
    app: 'api',
    headers: qaHeaders(),
  });
  return jsonOrThrow<QaHistoryResponse>(res);
}

/**
 * Ask a running test to stop. Cooperative: schedules notice within a couple of
 * seconds, so work already submitted still finishes in the target applications.
 */
export async function cancelQaRun(
  id: string,
): Promise<{ id: string; status: string; cancelled: boolean }> {
  const res = await apiFetch(`/v1/qa/runs/${encodeURIComponent(id)}/cancel`, {
    app: 'api',
    method: 'POST',
    headers: qaHeaders(),
  });
  return jsonOrThrow<{ id: string; status: string; cancelled: boolean }>(res);
}
